import React, { useEffect, useState } from 'react';

import { BiX } from 'react-icons/bi';
import { GoDash, GoPlus } from 'react-icons/go';

import { calcOrderPrice } from '../../../../utils/calcOrderPrice';
import { calcOrderPriceWOPromo } from '../../../../utils/calcOrderPriceWOPromo';
import { formateBrPrice } from '../../../../utils/formateBrPrice';

import Button from '../../../../newComponents/Button';
import Complement from '../Complement';

import * as S from '../ModalProduct/styles';
import TextArea from '../../../../newComponents/TextArea';
import { IProduct } from '../../../../interfaces/IProduct';
import { ICartItem } from '../../../../interfaces/ICartItem';
import { ISelectedComplement } from '../ModalProduct';

interface SimpleProductProps {
  onClose: () => void;
  product: IProduct;
  itemToCart: (payload: Omit<ICartItem, 'id'>) => void;
  cartItem?: ICartItem;
}

const SimpleProduct: React.FC<SimpleProductProps> = (props) => {
  const { onClose, product, itemToCart, cartItem } = props;

  const [price, setPrice] = useState(0);
  const [priceWithoutDiscount, setPriceWithoutDiscount] = useState(1);
  const [quantity, setQuantity] = useState(cartItem?.quantity ?? 1);
  const [note, setNote] = useState(cartItem?.observation || '');
  const [selectedComplements, setSelectedComplements] = useState<
    ISelectedComplement[]
  >(() => {
    if (cartItem && cartItem.compositeProduct.complements.length > 0)
      return cartItem.compositeProduct.complements;

    return [];
  });
  const [groupsUnderMinimum, setGroupsUnderMinimum] = useState<
    { id: number; under: boolean }[]
  >([]);
  const [anyGroupUnder, setAnyGroupUnder] = useState(false);

  const handleSelectedComplement = (
    cartComponent: ISelectedComplement,
  ): void => {
    const grupo = product.complements.find(
      (group) => cartComponent.complement.complementId === group.id,
    );

    if (!grupo) return;

    setSelectedComplements((old) => {
      let temp: ISelectedComplement[] = [];

      if (+grupo.maximumQuantity === 1) {
        temp = old.filter(
          (item) =>
            item.complement.id !== cartComponent.complement.id &&
            item.complement.complementId !==
              cartComponent.complement.complementId,
        );
      } else {
        temp = old.filter(
          (item) => item.complement.id !== cartComponent.complement.id,
        );
      }

      if (cartComponent.quantity > 0) temp.push(cartComponent);

      return temp;
    });
  };

  const handleAddItemToCart = (): void => {
    itemToCart({
      compositeProduct: {
        product,
        complements: selectedComplements,
      },
      childOrderItems: [],
      quantity,
      observation: note,
    });

    onClose();
  };

  useEffect(() => {
    setPrice(
      calcOrderPrice({
        id: product.id,
        compositeProduct: {
          product,
          complements: selectedComplements,
        },
        childOrderItems: [],
        quantity,
        observation: note,
      }),
    );
    setPriceWithoutDiscount(
      calcOrderPriceWOPromo({
        id: product.id,
        compositeProduct: {
          product,
          complements: selectedComplements,
        },
        childOrderItems: [],
        quantity,
        observation: note,
      }),
    );
  }, [quantity, selectedComplements, product, note]);

  useEffect(() => {
    product.complements.forEach((comp) => {
      if (groupsUnderMinimum !== undefined) {
        const tempGroupsUnder = groupsUnderMinimum;

        if (+comp.minimumQuantity > 0 && +comp.maximumQuantity > 1) {
          tempGroupsUnder.push({ id: comp.id, under: true });
        } else {
          tempGroupsUnder.push({ id: comp.id, under: false });
        }

        setGroupsUnderMinimum(tempGroupsUnder);
      } else if (+comp.minimumQuantity > 0 && +comp.maximumQuantity > 1) {
        setGroupsUnderMinimum([{ id: comp.id, under: true }]);
      } else {
        setGroupsUnderMinimum([{ id: comp.id, under: false }]);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  useEffect(() => {
    let counter = 0;

    if (groupsUnderMinimum !== undefined) {
      groupsUnderMinimum.forEach((group) => {
        if (group.under) {
          setAnyGroupUnder(true);
        } else {
          counter += 1;
        }
      });
      if (counter === groupsUnderMinimum.length) {
        setAnyGroupUnder(false);
      }
    }
  }, [groupsUnderMinimum]);

  return (
    <div className="body">
      <div>
        <S.ModalHeader>
          <h1>{product.name}</h1>
          <button type="button" onClick={onClose}>
            <BiX />
          </button>
        </S.ModalHeader>

        <S.ModalBody>
          <h2>{product?.observation}</h2>

          {product?.stock &&
            product?.stock.active &&
            Number(product?.stock.currentStock) === 0 && (
              <p className="out__of__stock">Sem Estoque</p>
            )}

          <div className="prices">
            {product?.discount?.active ? (
              <>
                <p className="old__value">
                  {formateBrPrice(+priceWithoutDiscount)}
                </p>
                <p className="discount__value">
                  {`${Math.round(+product.discount.value * 100 * -1).toFixed(
                    0,
                  )}% OFF`}
                </p>
                <p className="new__value">{formateBrPrice(+price)}</p>
              </>
            ) : (
              <p className="new__value">{formateBrPrice(+price)}</p>
            )}
          </div>

          <div className="complements">
            {product.complements
              .sort((a, b) => a.order - b.order)
              .map((complement) => (
                <Complement
                  key={complement.id}
                  cartComplement={cartItem?.compositeProduct.complements.find(
                    (comp) => comp.complement.complementId === complement.id,
                  )}
                  complement={complement}
                  handleSelectedComplement={handleSelectedComplement}
                  selectedComplements={selectedComplements}
                  groupsUnderMinimum={groupsUnderMinimum}
                  setGroupsUnderMinimum={setGroupsUnderMinimum}
                />
              ))}
          </div>

          <div className="quantity">
            <S.CustomIconButton
              size="small"
              disabled={quantity === 1}
              onClick={() => {
                if (quantity - 1 >= 0) {
                  setQuantity(quantity - 1)
                }
              }}
            >
              <GoDash />
            </S.CustomIconButton>
            {product.productPortion ? (
              <input
                type='number'
                value={quantity}
                onChange={e => {
                  const newValue = Number(e.target.value);

                  if (newValue >= 0) {
                    setQuantity(newValue);
                  }
                }}
              />
            ) : (
              <p>{quantity}</p>
            )}
            <S.CustomIconButton
              size="small"
              disabled={
                product?.stock &&
                product.stock.active &&
                Number(product.stock.currentStock) <= quantity
              }
              onClick={() => setQuantity(quantity + 1)}
            >
              <GoPlus />
            </S.CustomIconButton>
          </div>

          <div className="comments">
            <TextArea
              value={note}
              placeholder="Observação"
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </S.ModalBody>

        <S.ModalFooter>
          <Button
            full
            disabled={
              anyGroupUnder ||
              (product?.stock &&
                product.stock.active &&
                Number(product.stock.currentStock) < quantity)
            }
            onClick={handleAddItemToCart}
          >
            {`Adicionar ${formateBrPrice(+price)}`}
          </Button>
          <Button full variant="outlined" onClick={onClose}>
            Cancelar
          </Button>
        </S.ModalFooter>
      </div>
    </div>
  );
};

export default SimpleProduct;
